import { useState, useEffect } from 'react';

export interface NetworkStatus {
  isOnline: boolean;
  isReconnecting: boolean;
  lastOnlineAt: Date | null;
}

/**
 * Hook to reactively track browser network connectivity and transition states.
 * - `isOnline`: boolean indicating if browser has network connection.
 * - `isReconnecting`: boolean indicating temporary state when network is restored (e.g. 2.5s window).
 * - `lastOnlineAt`: timestamp of last confirmed online state.
 */
export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  });

  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);
  const [lastOnlineAt, setLastOnlineAt] = useState<Date | null>(() => {
    return typeof navigator !== 'undefined' && navigator.onLine ? new Date() : null;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const handleOnline = () => {
      setIsOnline(true);
      const now = new Date();
      setLastOnlineAt(now);
      setIsReconnecting(true);

      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      reconnectTimer = setTimeout(() => {
        setIsReconnecting(false);
      }, 2500);
    };

    const handleOffline = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      setIsOnline(false);
      setIsReconnecting(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
    };
  }, []);

  return {
    isOnline,
    isReconnecting,
    lastOnlineAt,
  };
}
